import { AUTO_CLOSE_NOTIFICATION_TIMEOUT } from "configs/AppConfig"
import { toast, ToastOptions, ToastPosition } from "react-toastify"
import { translate } from "utils/translate"

/**
 *  An ID for the logged out notification. The logged out notification is special
 *  cause we don't want to duplicate it on the screen. Internally, we want to
 *  manage change to this notification and make sure that there in only one
 *  visible to the user.
 */
const LOGGED_OUT_TOAST_ID = "loggedOutMessage"

export enum ToastVariant {
  INFO = "info",
  SUCCESS = "success",
  WARN = "warn",
  ERROR = "error",
}

interface Props extends Omit<ToastOptions, "variant" | "message" | "autoClose" | "position"> {
  message: string
  variant?: ToastVariant
  autoClose?: number
  position?: ToastPosition
}

// Keep track of active notification toasts to prevent duplicate (error) messages
const activeToasts = new Map<string, string>()

/** Creates a "toast" message (can be used for alerts or notifications) */
export const useToast = () => {
  const addToast = ({ variant = ToastVariant.INFO, message, autoClose = 5000, position = "top-right" }: Props) => {
    const options: ToastOptions = { autoClose, hideProgressBar: true, position }

    if (message) {
      let newToastId: string | number

      switch (variant) {
        case ToastVariant.SUCCESS:
          newToastId = toast.success(message, options)
          break
        case ToastVariant.WARN:
          newToastId = toast.warn(message, options)
          break
        case ToastVariant.ERROR:
          newToastId = toast.error(message, options)
          break
        case ToastVariant.INFO:
        default:
          newToastId = toast.info(message, options)
          break
      }

      activeToasts.set(message, newToastId.toString())

      return newToastId
    }
  }

  return { addToast }
}

const toastConfig = (props: Props) => ({
  ...props,
  autoClose: typeof props.autoClose === "number" ? props.autoClose : props.autoClose ? 5000 : props.autoClose,
  hideProgressBar: true,
  position: props.position ?? "top-right",
})

/** Helper to handle duplicate messages */
const handleDuplicateToasts = (
  message: string,
  toastFn: (message: string, config: ToastOptions) => string | number,
  props: Props
): string | number => {
  const existingId = activeToasts.get(message)
  if (existingId) {
    // Skip creating a new toast with identical message and return the existing ID
    return existingId
  }

  const config = {
    ...toastConfig(props),
    onClose: () => {
      activeToasts.delete(message)
    },
  }

  const newToastId = toastFn(message, config)
  activeToasts.set(message, newToastId.toString())

  return newToastId
}

export const notification: Record<ToastVariant, (props: Props) => string | number> &
  Record<"dismiss", (id: string) => void> = {
  info: (props: Props) => handleDuplicateToasts(props.message, toast.info, props),
  success: (props: Props) => handleDuplicateToasts(props.message, toast.success, props),
  warn: (props: Props) => handleDuplicateToasts(props.message, toast.warn, props),
  error: (props: Props) => handleDuplicateToasts(props.message, toast.error, props),
  dismiss: (id: string) => {
    // When dismissing a toast, also remove it from our `activeToasts` map
    activeToasts.forEach((value, key) => {
      if (value === id) {
        activeToasts.delete(key)
      }
    })
    toast.dismiss(id)
  },
}

/**
 *  An extension over the notification() function that automatically manages
 *  the dismissal of the logout notification and which message should be shown.
 */
export const logoutNotification = (manual: boolean) => {
  clearLogoutNotification()
  notification.info({
    message: translate({ id: `public.login.loggedOut.${manual ? "manually" : "automatically"}` }),
    autoClose: manual ? AUTO_CLOSE_NOTIFICATION_TIMEOUT : 0,
    toastId: LOGGED_OUT_TOAST_ID,
  })
}

/**
 *  Clears the logout notification if there is one active. If none is active,
 *  does nothing.
 */
export const clearLogoutNotification = () => {
  notification.dismiss(LOGGED_OUT_TOAST_ID)
}
